import { useState, useEffect, useCallback } from 'react';
import { Download, X, Smartphone, Share2, Zap } from 'lucide-react';

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const ua = navigator.userAgent;
    const ios = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    setIsIOS(ios);
    setIsMobile(/iPhone|iPad|iPod|Android/i.test(ua) || window.innerWidth < 768);

    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    // Daily snooze (was session-only): persist dismiss for 24h so users who
    // ignored once aren't bothered every reload, but get reminded the next day
    // until they actually install.
    let dismissedAt = null;
    try { dismissedAt = parseInt(localStorage.getItem('pwa-dismissed-at') || '0', 10); } catch { /* noop */ }
    const dismissedRecently = dismissedAt && (Date.now() - dismissedAt) < 24 * 60 * 60 * 1000;
    if (isStandalone || dismissedRecently) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowBanner(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // Show banner after 3s for ALL platforms if beforeinstallprompt hasn't fired
    const timer = setTimeout(() => {
      setShowBanner((prev) => {
        if (!prev) return true; // Show if not already showing
        return prev;
      });
    }, 3000);

    return () => { clearTimeout(timer); window.removeEventListener('beforeinstallprompt', handler); };
  }, []);

  // Expose install trigger for external buttons
  useEffect(() => {
    window.__pwaInstall = {
      canInstall: () => !!deferredPrompt || isIOS,
      trigger: () => {
        if (deferredPrompt) {
          deferredPrompt.prompt();
          deferredPrompt.userChoice.then((result) => {
            if (result.outcome === 'accepted') setShowBanner(false);
            setDeferredPrompt(null);
          });
        } else if (isIOS) {
          setShowIOSGuide(true);
        }
      }
    };
    // Dispatch event so other components know install state changed
    window.dispatchEvent(new Event('pwa-state-change'));
    return () => { delete window.__pwaInstall; };
  }, [deferredPrompt, isIOS]);

  const isAndroid = typeof navigator !== 'undefined' && /android/i.test(navigator.userAgent);

  const handleInstall = useCallback(async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const result = await deferredPrompt.userChoice;
      if (result.outcome === 'accepted') setShowBanner(false);
      setDeferredPrompt(null);
    } else {
      // No native prompt available, show platform guide
      setShowIOSGuide(true);
    }
  }, [deferredPrompt]);

  const handleDismiss = useCallback(() => {
    setShowBanner(false);
    setShowIOSGuide(false);
    try { localStorage.setItem('pwa-dismissed-at', String(Date.now())); } catch { /* noop */ }
  }, []);

  if (!showBanner && !showIOSGuide) return null;

  return (
    <>
      {showBanner && (
        <>
          {/* Desktop: compact bottom-right card (existing behavior) */}
          <div data-testid="install-app-banner" className={`${isMobile ? 'hidden' : 'block'} fixed bottom-4 right-4 z-50 max-w-[280px] md:max-w-sm animate-in slide-in-from-bottom-4 duration-500`}>
            <div className="bg-[#0f1923]/95 border border-[#2563eb]/30 rounded-xl p-3 shadow-lg backdrop-blur-xl relative">
              <button data-testid="install-dismiss-btn" onClick={handleDismiss} className="absolute top-2 right-2 text-gray-500 hover:text-white transition-colors">
                <X size={14} />
              </button>
              <div className="flex items-center gap-2.5 pr-4">
                <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-[#2563eb]/20 flex items-center justify-center">
                  <Smartphone className="text-[#2563eb]" size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-semibold text-xs">Installa EPT Italy</p>
                  <p className="text-gray-500 text-[10px] mt-0.5 leading-tight">Accesso rapido dalla home</p>
                </div>
              </div>
              <button data-testid="install-app-btn" onClick={handleInstall} className="mt-2 w-full flex items-center justify-center gap-1.5 bg-[#2563eb] hover:bg-[#1d4ed8] text-white text-xs font-semibold py-2 rounded-lg transition-all active:scale-[0.97]">
                <Download size={14} />
                {deferredPrompt ? 'Installa' : 'Come installare'}
              </button>
            </div>
          </div>

          {/* Mobile: full-width bottom sheet that explains WHY (anti-killing of background tab) */}
          <div data-testid="install-app-banner-mobile" className={`${isMobile ? 'block' : 'hidden'} fixed inset-x-0 bottom-0 z-50 px-3 pb-3 animate-in slide-in-from-bottom duration-500`}>
            <div className="bg-gradient-to-br from-[#0f1923]/98 to-[#1a0f1f]/98 border border-[#FF00E5]/40 rounded-2xl p-4 shadow-2xl backdrop-blur-xl relative" style={{ boxShadow: '0 0 32px rgba(255,0,229,0.18)' }}>
              <button data-testid="install-dismiss-btn-mobile" onClick={handleDismiss} className="absolute top-2.5 right-2.5 w-7 h-7 flex items-center justify-center rounded-full bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-colors">
                <X size={14} />
              </button>
              <div className="flex items-start gap-3 pr-7">
                <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br from-[#FF00E5]/25 to-[#00F0FF]/25 border border-[#FF00E5]/30 flex items-center justify-center">
                  <Zap className="text-[#FF00E5]" size={22} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-bold text-sm leading-tight">Installa EPT Italy</p>
                  <p className="text-gray-300 text-xs mt-1 leading-snug">
                    Così l'app <strong className="text-[#FF00E5]">non si chiude più</strong> quando passi ad altre app sul telefono.
                  </p>
                </div>
              </div>
              <button data-testid="install-app-btn-mobile" onClick={handleInstall} className="mt-3 w-full flex items-center justify-center gap-2 bg-gradient-to-r from-[#FF00E5] to-[#00F0FF] text-black text-sm font-bold py-3 rounded-xl transition-all active:scale-[0.97] shadow-lg">
                <Download size={16} />
                {deferredPrompt ? 'Installa adesso' : 'Vedi come installare'}
              </button>
            </div>
          </div>
        </>
      )}

      {showIOSGuide && (
        <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/60 backdrop-blur-sm" onClick={handleDismiss}>
          <div data-testid="ios-install-guide" className="bg-[#151921] border border-white/10 rounded-t-3xl p-6 w-full max-w-md animate-in slide-in-from-bottom duration-300" onClick={(e) => e.stopPropagation()}>
            <div className="w-10 h-1 bg-gray-600 rounded-full mx-auto mb-5" />
            <h3 className="text-white font-bold text-lg mb-4 flex items-center gap-2">
              <Smartphone size={20} className="text-[#2563eb]" />
              Installa EPT Italy
            </h3>
            {isIOS ? (
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#2563eb]/20 text-[#2563eb] font-bold text-sm flex items-center justify-center">1</span>
                  <p className="text-gray-300 text-sm">
                    Tocca il pulsante <Share2 size={14} className="inline text-[#2563eb]" /> <strong className="text-white">Condividi</strong> nella barra di Safari
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#2563eb]/20 text-[#2563eb] font-bold text-sm flex items-center justify-center">2</span>
                  <p className="text-gray-300 text-sm">Scorri e tocca <strong className="text-white">"Aggiungi alla schermata Home"</strong></p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#2563eb]/20 text-[#2563eb] font-bold text-sm flex items-center justify-center">3</span>
                  <p className="text-gray-300 text-sm">Tocca <strong className="text-white">"Aggiungi"</strong> in alto a destra</p>
                </div>
              </div>
            ) : isAndroid ? (
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#2563eb]/20 text-[#2563eb] font-bold text-sm flex items-center justify-center">1</span>
                  <p className="text-gray-300 text-sm">Apri il menu del browser (tre puntini <strong className="text-white">&#8942;</strong> in alto a destra)</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#2563eb]/20 text-[#2563eb] font-bold text-sm flex items-center justify-center">2</span>
                  <p className="text-gray-300 text-sm">Tocca <strong className="text-white">"Aggiungi a schermata Home"</strong> o <strong className="text-white">"Installa app"</strong></p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#2563eb]/20 text-[#2563eb] font-bold text-sm flex items-center justify-center">3</span>
                  <p className="text-gray-300 text-sm">Conferma toccando <strong className="text-white">"Installa"</strong></p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#2563eb]/20 text-[#2563eb] font-bold text-sm flex items-center justify-center">1</span>
                  <p className="text-gray-300 text-sm">Su <strong className="text-white">Chrome</strong>: cerca l'icona <Download size={14} className="inline text-[#2563eb]" /> nella barra degli indirizzi</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#2563eb]/20 text-[#2563eb] font-bold text-sm flex items-center justify-center">2</span>
                  <p className="text-gray-300 text-sm">Oppure apri il menu (tre puntini <strong className="text-white">&#8942;</strong>) e seleziona <strong className="text-white">"Installa EPT Italy"</strong></p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#2563eb]/20 text-[#2563eb] font-bold text-sm flex items-center justify-center">3</span>
                  <p className="text-gray-300 text-sm">Clicca <strong className="text-white">"Installa"</strong> nella finestra di conferma</p>
                </div>
              </div>
            )}
            <button onClick={handleDismiss} className="mt-6 w-full py-3 bg-white/10 hover:bg-white/15 text-white text-sm font-semibold rounded-xl transition-colors">Ho capito</button>
          </div>
        </div>
      )}
    </>
  );
}
